import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Icon from '@welldev-group/rcl-icon';
import Card from '@welldev-group/rcl-card';
import IconButton from '@welldev-group/rcl-icon-button';
import { I18nText } from '@welldev-group/react-comp-lib';
import Alert from '@welldev-group/rcl-alert';
import PaginationWithTranslation from '../../../shared/components/Pagination/PaginationWithTranslation';
import TeamModal from './TeamModal';

const ListTable = props => {
  const [deleteInProgress, setDeleteInProgress] = useState({});
  const [flashText, setFlashText] = useState(null);
  const {
    signedInAsSuperAdmin,
    currentUsersAffiliateId,
    fetchTeamsByAffiliates,
    teams,
    affiliateId,
    searchParam,
    sortDirection,
    currentPage,
    totalPages,
    setCurrentPage,
    fetchTeams,
    deleteTeam,
  } = props;

  useEffect(() => {
    setFlashText(null);
  }, [currentPage]);

  const teamInitialAttributes = team => ({
    id: team.id,
    name: team.name,
    affiliate: {
      label: team.affiliate.clientName,
      value: team.affiliate.id,
    },
    users: team.users.map(user => ({
      label: user.name,
      value: user.id,
    })),
  });

  return (
    <div className="grid align-center">
      <Card version="v2" size="full">
        <Alert
          className="team-index__flash col-grid direction-row col-12"
          isVisible={!!flashText}
          type="success"
          hideClose={true}
        >
          {flashText}
        </Alert>
        {teams.length > 0 ? (
          <>
            <div className="team-index__list-headers hidden-md hidden-sm hidden-xs hidden-xxs">
              <div className="col-10 col-bleed">
                <div className="team-index__list-id col-4">
                  <I18nText id="team.list.header.id" />
                </div>
                <div className="team-index__list-name col-4">
                  <I18nText id="team.list.header.name" />
                </div>
                <div className="team-index__list-members-count col-2">
                  <I18nText id="team.list.header.members" />
                </div>
              </div>
              <div className="col-2 team-index__list-actions">
                <I18nText id="team.list.header.actions" />
              </div>
            </div>
            <div className="team-index__list-values">
              {teams.map(team => (
                <Card version="v2" size="full" key={team.id}>
                  <div className="team-index__list-row">
                    <div className="col-lg-10 col-md-9 col-8 col-bleed">
                      <div className="team-index__list-id col-lg-4 col-md-6 col-sm-12">
                        {team.id}
                        <div className="team-index__header team-index__header--small">
                          <I18nText id="team.list.header.id" />
                        </div>
                      </div>
                      <div className="team-index__list-name col-lg-4 col-md-6 col-sm-6">
                        {team.name}
                        <div className="team-index__header team-index__header--small">
                          <I18nText id="team.list.header.name" />
                        </div>
                      </div>
                      <div className="team-index__list-members-count col-lg-2 col-md-4 col-sm-6">
                        {team.membersCount}
                        <div className="team-index__header team-index__header--small">
                          <I18nText id="team.list.header.members" />
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-2 col-md-3 col-4 col-bleed team-index__list-actions">
                      <TeamModal
                        signedInAsSuperAdmin={signedInAsSuperAdmin}
                        currentUsersAffiliateId={currentUsersAffiliateId}
                        fetchTeamsByAffiliates={fetchTeamsByAffiliates}
                        isNewRecord={false}
                        teamInitialAttributes={teamInitialAttributes(team)}
                        buttonClass="team-index__edit"
                        buttonText={
                          <IconButton
                            isIconOnly={true}
                            icon={<Icon name="edit" />}
                            size="large"
                            label="Edit"
                          />
                        }
                        header={<I18nText id="team.actions.edit" />}
                      />
                      <IconButton
                        version="v2"
                        isIconOnly={true}
                        icon={<Icon name="delete" />}
                        size="large"
                        onClick={() => {
                          setDeleteInProgress(prevState => ({
                            ...prevState,
                            [team.id]: true,
                          }));
                          deleteTeam(team.id).then(({ data }) => {
                            setFlashText(data.message);
                          });
                        }}
                        disabled={deleteInProgress[team.id]}
                        label={<I18nText id="team.actions.delete" />}
                      />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </>
        ) : (
          <div className="col-12">
            <I18nText id="team.list.no_teams" />
          </div>
        )}
        <div className="col-12 col-bleed">
          <PaginationWithTranslation
            currentPage={currentPage}
            totalPages={totalPages}
            onPaginationClick={value => {
              setCurrentPage(prevState => {
                const newState = { ...prevState };
                newState[affiliateId] = value;
                return newState;
              });
              fetchTeams(value, searchParam, affiliateId, sortDirection);
            }}
          />
        </div>
      </Card>
    </div>
  );
};

ListTable.defaultProps = {
  sortDirection: null,
};

ListTable.propTypes = {
  signedInAsSuperAdmin: PropTypes.bool.isRequired,
  currentUsersAffiliateId: PropTypes.number.isRequired,
  fetchTeamsByAffiliates: PropTypes.func.isRequired,
  teams: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      membersCount: PropTypes.number,
      affiliate: PropTypes.shape({
        id: PropTypes.number,
        clientName: PropTypes.string,
      }),
      users: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          avatar: PropTypes.string,
          name: PropTypes.string,
          email: PropTypes.string,
          teamsAsSentence: PropTypes.string,
          lastActive: PropTypes.string,
          deactivated: PropTypes.bool,
        }),
      ),
    }),
  ).isRequired,
  affiliateId: PropTypes.number.isRequired,
  searchParam: PropTypes.string.isRequired,
  sortDirection: PropTypes.string,
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  fetchTeams: PropTypes.func.isRequired,
  deleteTeam: PropTypes.func.isRequired,
};

export default ListTable;
